import { useCallback, useState } from 'react';
import { Upload, X, FileIcon, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { FormField } from '@/components/forms';
import { uploadFile } from '@/lib/upload';
import type { BroadcastMedia } from '../types';

const ACCEPT_MAP: Record<BroadcastMedia['type'], string> = {
  photo: 'image/*',
  video: 'video/*',
  document: '*/*',
};

interface MediaUploaderProps {
  mediaType: BroadcastMedia['type'];
  value: string;
  onChange: (url: string) => void;
  disabled?: boolean;
}

export function MediaUploader({ mediaType, value, onChange, disabled }: MediaUploaderProps) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const accept = ACCEPT_MAP[mediaType];

  const handleUpload = useCallback(
    async (file: File) => {
      setUploading(true);
      setProgress(0);
      setError(null);

      try {
        const result = await uploadFile({
          file,
          access: 'PUBLIC',
          onProgress: ({ percent }) => setProgress(percent),
        });
        onChange(result.url);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Ошибка загрузки');
      } finally {
        setUploading(false);
      }
    },
    [onChange],
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleUpload(file);
      }
      e.target.value = '';
    },
    [handleUpload],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) {
        handleUpload(file);
      }
    },
    [handleUpload],
  );

  const handleClear = () => {
    onChange('');
    setError(null);
  };

  const isImage = mediaType === 'photo' && value && !uploading;

  return (
    <div className="space-y-3">
      {value && !uploading && (
        <div className="relative rounded-lg border bg-muted/30 overflow-hidden">
          {isImage ? (
            <div className="relative">
              <img
                src={value}
                alt="Медиа-вложение"
                className="w-full max-h-64 object-contain bg-black/5"
              />
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="absolute top-2 right-2 h-7 w-7"
                onClick={handleClear}
                disabled={disabled}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-3 p-3">
              <FileIcon className="h-8 w-8 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{value}</p>
                <p className="text-xs text-muted-foreground">
                  {mediaType === 'video' ? 'Видео' : 'Документ'}
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="shrink-0 h-8 w-8"
                onClick={handleClear}
                disabled={disabled}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}

      {!value && !uploading && (
        <div
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          className={`
            relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
            ${isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'}
            ${disabled ? 'opacity-50 pointer-events-none' : ''}
          `}
        >
          <input
            type="file"
            accept={accept}
            onChange={handleFileSelect}
            disabled={disabled}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
          <div className="flex flex-col items-center gap-2">
            <Upload className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {isDragging ? 'Отпустите для загрузки' : 'Перетащите файл или нажмите для выбора'}
            </p>
            <p className="text-xs text-muted-foreground">
              {mediaType === 'photo' && 'JPG, PNG, WebP'}
              {mediaType === 'video' && 'MP4, MOV, AVI'}
              {mediaType === 'document' && 'Любой тип файла'}
            </p>
          </div>
        </div>
      )}

      {uploading && (
        <div className="border rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <div className="flex-1">
              <div className="flex justify-between text-sm mb-1">
                <span>Загрузка...</span>
                <span className="text-muted-foreground">{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          </div>
        </div>
      )}

      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}

      <FormField label="" name="mediaUrl">
        <div className="flex gap-2">
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Или вставьте URL вручную"
            disabled={disabled || uploading}
            className="text-xs"
          />
        </div>
      </FormField>
    </div>
  );
}
